import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App, { LoadingScreen } from './App';
import reportWebVitals from './reportWebVitals';
import { MsalProvider } from '@azure/msal-react';

import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { Provider } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { SnackbarProvider } from 'notistack';
import store from './stateManagement/store';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/hu';
import { IconButton, Tooltip } from '@mui/material';
import { Close } from '@mui/icons-material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import axios from 'axios';
import authService, { isPublicAuthRequestUrl } from './services/authService';

// Capture invitation code before MSAL OAuth cleanup can rewrite the hash.
(() => {
  try {
    const hash = window.location.hash || '';
    const match = hash.match(/\/invitations\/accept\?[^#]*code=([^&]+)/i);
    if (match?.[1]) {
      sessionStorage.setItem(
        'pendingInvitationCode',
        decodeURIComponent(match[1])
      );
    }
  } catch {
    // ignore
  }
})();

const DNDProvider = DndProvider as any;
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const notistackRef = React.createRef<any>();
const onClickDismiss = (key: any) => {
  notistackRef.current.closeSnackbar(key);
};

axios.interceptors.response.use(
  (resp) => {
    return resp;
  },
  async (error) => {
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return {
        data: {
          canceled: true,
          hasError: false,
          errorMessages: [],
        },
      };
    }

    const status = error.response?.status;
    const config = error.config as
      | (typeof error.config & { _cmsRetryAfterRefresh?: boolean })
      | undefined;
    const requestUrl = config?.url as string | undefined;

    if (
      status === 401 &&
      !isPublicAuthRequestUrl(requestUrl) &&
      !config?._cmsRetryAfterRefresh
    ) {
      if (authService.isLocalAuth() && authService.hasRefreshToken()) {
        const refreshed = await authService.refreshSession();
        if (refreshed && config) {
          config._cmsRetryAfterRefresh = true;
          const token = localStorage.getItem('cmsWebApiAccessToken');
          if (token) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
          }
          return axios(config);
        }
      }

      if (authService.hasLocalToken() || authService.hasRefreshToken()) {
        void authService.endExpiredSession();
      }
    } else if (
      status === 401 &&
      config?._cmsRetryAfterRefresh &&
      (authService.hasLocalToken() || authService.hasRefreshToken())
    ) {
      void authService.endExpiredSession();
    }

    // Convert HTTP errors into the app's ResponseBase shape so callers
    // using .then() without .catch() don't end as Uncaught (in promise).
    if (error.response) {
      const data = error.response.data;
      if (typeof data === 'string' && data.trim()) {
        return {
          data: {
            hasError: true,
            httpStatus: error.response.status,
            errorMessages: [data],
          },
        };
      }
      if (data && typeof data === 'object') {
        const modelErrors =
          data.errors && typeof data.errors === 'object'
            ? Object.values(data.errors).flat().filter(Boolean)
            : [];
        return {
          data: {
            ...data,
            hasError: true,
            httpStatus: error.response.status,
            errorMessages:
              data.errorMessages ??
              (modelErrors.length > 0 ? modelErrors : null) ??
              (data.title ? [data.title] : null) ??
              (data.message ? [data.message] : null) ??
              [error.message || `HTTP ${error.response.status}`],
          },
        };
      }
      return {
        data: {
          hasError: true,
          httpStatus: error.response.status,
          errorMessages: [error.message || `HTTP ${error.response.status}`],
        },
      };
    }

    return {
      data: {
        canceled: false,
        hasError: true,
        errorMessages: [error.message || 'Ismeretlen hálózati hiba'],
      },
    };
  }
);

const hasOAuthParams = (url: string): boolean => {
  // Invitation links use `#/invitations/accept?code=...` — must not be treated as MSAL OAuth.
  if (/\/invitations\/accept/i.test(url)) {
    return false;
  }
  // App HashRouter routes look like `#/path?...` — not an OAuth fragment.
  const hash = url.includes('#') ? url.slice(url.indexOf('#')) : url;
  if (/^#\/.+/.test(hash)) {
    return false;
  }
  return (
    /(?:^|[?#&])code=/.test(url) ||
    url.includes('id_token=') ||
    url.includes('access_token=') ||
    url.includes('session_state=') ||
    url.includes('error=')
  );
};

/** Remove OAuth callback fragment so browser back does not re-hit the redirect URL. */
const removeOAuthRedirectFromHistory = () => {
  const hash = window.location.hash;
  if (!hasOAuthParams(hash)) return;
  const baseUrl = window.location.origin + window.location.pathname;
  window.history.replaceState(null, '', baseUrl + '#/');
  window.history.pushState({ oauthCleaned: true }, '', baseUrl + '#/');
};

const RootComponent = () => {
  const [msalInstance, setMsalInstance] =
    useState<PublicClientApplication | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handlePopState = () => {
      if (hasOAuthParams(window.location.hash)) {
        const baseUrl = window.location.origin + window.location.pathname;
        window.history.replaceState(null, '', baseUrl + '#/');
        window.history.pushState(null, '', baseUrl + '#/');
      }
    };
    window.addEventListener('popstate', handlePopState);

    const initializeApp = async () => {
      try {
        timeoutId = setTimeout(() => {
          if (isMounted) {
            console.warn('MSAL initialization timeout - proceeding anyway');
            setInitError('Initializálás időtúllépés');
            setIsInitialized(true);
          }
        }, 30000);

        const instance = new PublicClientApplication(msalConfig);
        await instance.initialize();
        if (!isMounted) return;

        try {
          const response = await instance.handleRedirectPromise();
          removeOAuthRedirectFromHistory();

          if (response?.account) {
            instance.setActiveAccount(response.account);
          } else {
            const accounts = instance.getAllAccounts();
            if (accounts.length > 0 && !instance.getActiveAccount()) {
              instance.setActiveAccount(accounts[0]);
            }
          }
        } catch (error) {
          console.error('MSAL redirect handling error:', error);
          removeOAuthRedirectFromHistory();
        }

        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (isMounted) {
          setMsalInstance(instance);
          setIsInitialized(true);
          setInitError(null);
        }
      } catch (error: any) {
        console.error('MSAL initialization error:', error);
        if (isMounted) {
          setInitError(
            error?.message ||
              'Ismeretlen hiba történt az inicializálás során'
          );
          setIsInitialized(true);
        }
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    initializeApp();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  if (!isInitialized) {
    return <LoadingScreen message="Hitelesítés betöltése..." />;
  }

  let instanceToUse = msalInstance;
  if (!instanceToUse && msalConfig) {
    try {
      instanceToUse = new PublicClientApplication(msalConfig);
      console.warn('Using fallback MSAL instance due to initialization error');
    } catch (error) {
      console.error('Failed to create fallback MSAL instance:', error);
      return (
        <LoadingScreen
          message={
            initError ||
            'Hitelesítés inicializálása sikertelen. Kérjük, frissítse az oldalt.'
          }
        />
      );
    }
  }

  if (!instanceToUse) {
    return (
      <LoadingScreen
        message={initError || 'Hitelesítés inicializálása...'}
      />
    );
  }

  return (
    <MsalProvider instance={instanceToUse}>
      <Provider store={store}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu">
          <SnackbarProvider
            maxSnack={3}
            autoHideDuration={5000}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            ref={notistackRef}
            action={(key: any) => (
              <Tooltip title={<span>Close</span>}>
                <IconButton
                  aria-label="delete"
                  size="small"
                  color="inherit"
                  onClick={() => onClickDismiss(key)}
                >
                  <Close fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          >
            <DNDProvider backend={HTML5Backend}>
              <App />
            </DNDProvider>
          </SnackbarProvider>
        </LocalizationProvider>
      </Provider>
    </MsalProvider>
  );
};

root.render(
  <React.StrictMode>
    <RootComponent />
  </React.StrictMode>
);

reportWebVitals();
